import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Btn } from "../UI";
import { SessionMedsSnapshot } from "../SessionMedsSnapshot";
import { RefillRequestDialog } from "../rx/RefillRequestDialog";
import { ListChecks, RefreshCw, Clock } from "lucide-react";

const LIST_ROLES = ["admin", "clinician", "front_desk"];

const refillable = (m) => ["active", "on hold", ""].includes((m.status || "").trim().toLowerCase());

/** Latest session meds and a shortcut to the Master Medication List, shown above the chart Medications cards. */
export function MedQuickTools({ client, onOpenMasterList }) {
  const [snap, setSnap] = useState(null);
  const toggle = () => (snap ? setSnap(null) : api.get(`/clients/${client.id}/session-meds`).then((r) => setSnap(r.data)).catch((e) => toast.error(apiError(e))));
  return (
    <div className="mb-4" data-testid="med-panel-tools">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Btn variant="outline" className="rounded-lg" data-testid="med-panel-session-meds-toggle" aria-expanded={!!snap} onClick={toggle}>{snap ? "Hide latest session meds" : "Latest session meds"}</Btn>
        {onOpenMasterList && <Btn variant="ghost" className="rounded-lg" data-testid="med-panel-open-master-list" onClick={onOpenMasterList}><ListChecks size={14} /> Open Master Medication List</Btn>}
      </div>
      {snap && <div className="mt-3"><SessionMedsSnapshot data={snap} /></div>}
    </div>
  );
}

/** Pending refill requests for one client, plus the dialog to log a new one. Billing does not see refill requests. */
export function useRefills(client) {
  const { user } = useAuth();
  const allowed = LIST_ROLES.includes(user?.role) || !!user?.is_super_admin;
  const [pending, setPending] = useState({});
  const [med, setMed] = useState(null);
  const load = useCallback(() => (allowed ? api.get("/refill-requests", { params: { client_id: client.id, status: "pending" } })
    .then((r) => setPending(Object.fromEntries(r.data.map((x) => [x.medication_id, x])))).catch(() => setPending({})) : Promise.resolve()), [client.id, allowed]);
  useEffect(() => { load(); }, [load]);
  const dialog = med && <RefillRequestDialog client={client} med={med} onClose={() => setMed(null)} onDone={() => { setMed(null); load(); }} />;
  return { pending, open: setMed, dialog };
}

export function RefillCardAction({ m, pending, canLog, onOpen }) {
  if (pending) return <span data-testid={`med-panel-refill-pending-${m.id}`} className="mr-auto inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2.5 py-1 text-[11px] font-bold text-[#8D5A00]"><Clock size={11} /> Refill request pending</span>;
  if (!canLog || !refillable(m)) return null;
  return (
    <button type="button" data-testid={`med-panel-refill-${m.id}`} onClick={() => onOpen(m)}
      className="mr-auto inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD3] bg-white px-3 py-1 text-[11px] font-semibold text-[#4E342E] transition-colors hover:bg-[#EDE7DC]">
      <RefreshCw size={12} /> Log refill request
    </button>
  );
}
